/**
 * Proactive access-token refresh — fires before JWT expiry so clients
 * do not rely solely on reactive 401 handling (which amplifies multi-tab
 * races at the 15-minute boundary).
 */
import { getJwtExpiresInSeconds, PROACTIVE_REFRESH_LEAD_SECONDS } from './jwtSchedule';

let scheduledTimer: ReturnType<typeof setTimeout> | null = null;
let proactiveRefreshRunner: (() => Promise<string | null>) | null = null;

export function registerProactiveRefreshRunner(
  runner: () => Promise<string | null>,
): void {
  proactiveRefreshRunner = runner;
}

export function clearScheduledAccessTokenRefresh(): void {
  if (scheduledTimer) {
    clearTimeout(scheduledTimer);
    scheduledTimer = null;
  }
}

/**
 * Schedule a silent refresh ~2 minutes before the access token expires.
 * No-op when `exp` is missing or already past the lead window.
 */
export function scheduleAccessTokenRefresh(accessToken: string | null): void {
  clearScheduledAccessTokenRefresh();
  if (!accessToken || !proactiveRefreshRunner) return;

  const expiresIn = getJwtExpiresInSeconds(accessToken);
  if (expiresIn === null) return;

  const delaySeconds = expiresIn - PROACTIVE_REFRESH_LEAD_SECONDS;
  if (delaySeconds <= 0) {
    void proactiveRefreshRunner();
    return;
  }

  scheduledTimer = setTimeout(() => {
    scheduledTimer = null;
    void proactiveRefreshRunner?.();
  }, delaySeconds * 1000);
}
